/* ==========================================================================
   BRACELY — Knee Sleeves PDP: pack selector behaviour
   --------------------------------------------------------------------------
   Sleeve 1 is Horizon's own variant picker. Keeping it means the media
   gallery still filters to the chosen colourway, the price block still
   updates, and express checkout still works for a single sleeve.

   Sleeves 2 and 3 are extra rows rendered by this feature. Choosing a pack
   size reveals them; each carries its own colour and size, and each is added
   to the cart as its own line at quantity 1. The pack price comes from the
   automatic quantity discounts in Shopify Admin, so what the card shows is
   what the cart charges.

   Express checkout is hidden for 2 and 3 packs: it can only ever buy the one
   variant held by the form, so leaving it visible would let a customer pay
   for one sleeve after choosing three.

   Defensive throughout — if the markup it expects is missing it leaves the
   page alone rather than breaking the single-sleeve path that already works.
   ========================================================================== */

(function () {
  'use strict';

  var TEMPLATE = 'product.knee-sleeves';
  var HIDDEN_COLOURS = ['Green', 'Orange'];
  var DEFAULT_COLOUR = 'Black';

  function onThisTemplate() {
    var main = document.getElementById('MainContent');
    return !!main && main.getAttribute('data-template') === TEMPLATE;
  }

  function parseJSON(text, fallback) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  }

  /* ----------------------------------------------------------------------
     Sleeve 1 — read the variant the theme's own picker currently holds.
     ---------------------------------------------------------------------- */

  function findProductForm(root) {
    return (
      root.closest('product-form-component, product-form, .shopify-product-form') ||
      document.querySelector('product-form-component, product-form, .shopify-product-form') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function firstSleeveVariantId(form) {
    if (form) {
      var input = form.querySelector('input[name="id"], select[name="id"]');
      if (input && input.value) return String(input.value).trim();
    }

    // Fallback: the variant picker prints the selected variant as JSON.
    var picker = document.querySelector('variant-picker script[type="application/json"]');
    if (picker) {
      var variant = parseJSON(picker.textContent, null);
      if (variant && variant.id) return String(variant.id);
    }

    return null;
  }

  /* ----------------------------------------------------------------------
     Hide the colourways we don't merchandise. CSS does this too; this is the
     backstop, and it also moves the selection off a hidden colour if someone
     arrives on a Green or Orange variant by direct link.
     ---------------------------------------------------------------------- */

  function hideUnmerchandisedColours() {
    var inputs = document.querySelectorAll('variant-picker input[type="radio"]');
    var needsReset = null;

    Array.prototype.forEach.call(inputs, function (input) {
      if (HIDDEN_COLOURS.indexOf(input.value) === -1) return;

      var label = input.closest('label');
      if (label) label.classList.add('brc-colour-hidden');
      if (input.checked) needsReset = input;
    });

    if (!needsReset) return;

    var fallback = document.querySelector(
      'variant-picker input[type="radio"][value="' + DEFAULT_COLOUR + '"]'
    );
    if (fallback && !fallback.checked) fallback.click();
  }

  /* ----------------------------------------------------------------------
     Extra sleeve rows
     ---------------------------------------------------------------------- */

  function rowState(row, variants) {
    var colourBtn = row.querySelector('[data-brc-colour].is-active');
    var sizeSel = row.querySelector('[data-brc-size]');

    var colour = colourBtn ? colourBtn.getAttribute('data-brc-colour') : null;
    var size = sizeSel ? sizeSel.value : null;
    if (!colour || !size) return { colour: colour, size: size, variant: null };

    return {
      colour: colour,
      size: size,
      variant: variants[size + '|' + colour] || null
    };
  }

  function paintRow(row, variants) {
    var state = rowState(row, variants);
    var chosen = row.querySelector('[data-brc-chosen]');
    var err = row.querySelector('[data-brc-err]');

    if (chosen) {
      chosen.textContent = state.colour && state.size ? state.colour + ' · ' + state.size : '';
    }

    var unavailable = !state.variant || state.variant.available === false;
    if (err) err.hidden = !unavailable;

    return state;
  }

  function initRow(row, variants, onChange) {
    var swatches = Array.prototype.slice.call(row.querySelectorAll('[data-brc-colour]'));

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        swatches.forEach(function (other) {
          var active = other === btn;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        paintRow(row, variants);
        onChange();
      });
    });

    var sizeSel = row.querySelector('[data-brc-size]');
    if (sizeSel) {
      sizeSel.addEventListener('change', function () {
        paintRow(row, variants);
        onChange();
      });
    }

    paintRow(row, variants);
  }

  /* ----------------------------------------------------------------------
     Wiring
     ---------------------------------------------------------------------- */

  function initPack(pack) {
    var script = pack.querySelector('[data-brc-variants]');
    var variants = script ? parseJSON(script.textContent, {}) : {};
    if (!variants || !Object.keys(variants).length) return;

    var tiers = Array.prototype.slice.call(pack.querySelectorAll('[data-brc-tier]'));
    var rows = Array.prototype.slice.call(pack.querySelectorAll('[data-brc-row]'));
    if (!tiers.length) return;

    var form = findProductForm(pack);
    var quantity = form ? form.querySelector('input[name="quantity"]') : null;
    var count = 1;

    function applyTier(next) {
      count = next;

      tiers.forEach(function (tier) {
        var active = parseInt(tier.getAttribute('data-brc-tier'), 10) === count;
        tier.classList.toggle('is-active', active);
        tier.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      rows.forEach(function (row) {
        var index = parseInt(row.getAttribute('data-brc-row'), 10);
        var shown = index <= count;
        row.classList.toggle('is-shown', shown);
        // Hidden rows must not be read by the screen reader or tabbed into.
        row.setAttribute('aria-hidden', shown ? 'false' : 'true');
        Array.prototype.forEach.call(row.querySelectorAll('button, select'), function (el) {
          if (shown) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      });

      // Sleeve 1 always goes through the theme's own quantity input at 1.
      if (quantity) {
        quantity.value = '1';
        quantity.dispatchEvent(new Event('input', { bubbles: true }));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (form) form.classList.toggle('brc-hide-express', count > 1);

      document.dispatchEvent(
        new CustomEvent('bracely:pack-change', { detail: { count: count } })
      );
    }

    tiers.forEach(function (tier) {
      tier.addEventListener('click', function () {
        var next = parseInt(tier.getAttribute('data-brc-tier'), 10);
        if (!isNaN(next)) applyTier(next);
      });
    });

    rows.forEach(function (row) {
      initRow(row, variants, function () {});
    });

    applyTier(1);

    /* ------------------------------------------------------------------
       Add to cart. Single sleeves keep the theme's own submit path, drawer
       and all. Packs are intercepted, because one form submit cannot carry
       three different variants.
       ------------------------------------------------------------------ */

    if (!form) return;

    form.addEventListener(
      'submit',
      function (event) {
        if (count < 2) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        var items = [];
        var missing = false;

        var firstId = firstSleeveVariantId(form);
        if (!firstId) {
          missing = true;
        } else {
          items.push({ id: firstId, quantity: 1 });
        }

        rows.forEach(function (row) {
          var index = parseInt(row.getAttribute('data-brc-row'), 10);
          if (index > count) return;

          var state = paintRow(row, variants);
          if (!state.variant || state.variant.available === false) {
            missing = true;
            return;
          }
          items.push({ id: String(state.variant.id), quantity: 1 });
        });

        if (missing || items.length !== count) return;

        var button = form.querySelector('button[type="submit"], [type="submit"]');
        if (button) button.setAttribute('aria-busy', 'true');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add failed');
            return response.json();
          })
          .then(function () {
            window.location.href = '/cart';
          })
          .catch(function () {
            if (button) button.removeAttribute('aria-busy');
            var note = pack.querySelector('[data-brc-pack-error]');
            if (note) note.hidden = false;
          });
      },
      true
    );
  }

  function init() {
    if (!onThisTemplate()) return;

    hideUnmerchandisedColours();

    var packs = document.querySelectorAll('[data-brc-pack]');
    Array.prototype.forEach.call(packs, initPack);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Horizon re-renders the product section when the variant changes, which
  // rebuilds the picker markup — re-apply the colour hiding when it does.
  document.addEventListener('variant:update', hideUnmerchandisedColours);
  document.addEventListener('shopify:section:load', init);
})();

/* ==========================================================================
   BRACELY — Knee Recovery Device add-on card
   --------------------------------------------------------------------------
   Adds the real device product to the cart, and keeps its displayed price
   honest: the 10 pound bundle saving applies alongside 1 or 2 sleeves, but
   Shopify will not apply it on top of the 3+ sleeve quantity discount, so at
   3 sleeves the card falls back to the standalone price rather than promising
   a saving the checkout will not give.
   ========================================================================== */

(function () {
  'use strict';

  function init() {
    var card = document.querySelector('[data-brc-device-card]');
    if (!card) return;

    var full = card.getAttribute('data-brc-device-full') || '';
    var bundle = card.getAttribute('data-brc-device-bundle') || '';
    var now = card.querySelector('[data-brc-device-now]');
    var terms = card.querySelector('[data-brc-device-terms]');
    var error = card.querySelector('[data-brc-device-error]');
    var button = card.querySelector('[data-brc-device-add]');

    function setPackSize(count) {
      var standalone = count >= 3;
      card.classList.toggle('is-standalone', standalone);

      if (now) now.textContent = standalone ? full : bundle;

      if (terms) {
        terms.textContent = standalone
          ? full + ' with the 3 pack — the bundle saving applies to orders of one or two sleeves.'
          : full + ' on its own, ' + bundle + ' added to your sleeves.';
      }
    }

    document.addEventListener('bracely:pack-change', function (event) {
      var count = event && event.detail ? event.detail.count : 1;
      setPackSize(count);
    });

    setPackSize(1);

    if (!button) return;

    button.addEventListener('click', function () {
      var id = button.getAttribute('data-brc-device-add');
      if (!id) return;

      if (error) error.hidden = true;
      button.setAttribute('aria-busy', 'true');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: String(id), quantity: 1 }] })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function () {
          window.location.href = '/cart';
        })
        .catch(function () {
          button.removeAttribute('aria-busy');
          if (error) error.hidden = false;
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
